// src/App.jsx
import React, { Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import { ThemeProvider } from "next-themes";

import RootLayout from "./layouts/RootLayout";
import HomePage from "./pages/HomePage";
import MatchesPage from "./pages/MatchesPage";
import NotFoundPage from "./pages/NotFoundPage";
import AuthPage from "./pages/AuthPage";
import DashboardPage from "./pages/DashboardPage";
import RequireAuth from "./components/RequireAuth";
import ScrollManager from "./components/ScrollManager";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import AboutPublicPage from "./pages/AboutPublicPage";

import { AuthProvider } from "./contexts/AuthContext";

import AccountPage from "@/pages/account/AccountPage";
import ProfilePage from "@/pages/account/ProfilePage";
import AppearancePage from "@/pages/account/AppearancePage";
import NotificationsPage from "@/pages/account/NotificationsPage";
import AnnouncementsPage from "@/pages/account/AnnouncementsPage";
import SecurityPage from "@/pages/account/SecurityPage";
import PrivacyPage from "@/pages/account/PrivacyPage";
import AboutPage from "@/pages/account/AboutPage";
import MatchProgramsPage from "@/pages/account/MatchProgramsPage";
import LegalPage from "@/pages/account/LegalPage";
import ProtocolSettingsPage from "@/pages/account/ProtocolSettingsPage";
// import SessionGuard from "@/components/SessionGuard";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import queryClient from "@/lib/queryClient";

import RequireAdmin from "./contexts/RequireAdmin";
import AdminHomePage from "./pages/AdminHomePage";
import AdsListPage from "./pages/AdsListPage";
import AdFormPage from "./pages/AdFormPage";
import AdCustomerDetailPage from "./pages/AdCustomerDetailPage";
import AdminLineupsPage from "./pages/AdminLineupsPage";
import AdminMatchStructurePage from "./pages/AdminMatchStructurePage";
import AdminNotificationsPage from "./pages/AdminNotificationsPage";
import AdminGuestRidersPage from "./pages/AdminGuestRidersPage";
import AdminDoubleMeetingBonusPage from "./pages/AdminDoubleMeetingBonusPage";
import AdminPlayoffSeriesResolutionsPage from "./pages/AdminPlayoffSeriesResolutionsPage";
import AdminAppReleasesPage from "./pages/AdminAppReleasesPage";

import SavedMatchDetailPage from "./pages/SavedMatchDetailPage";
import OfficialMatchDetailPage from "./pages/OfficialMatchDetailPage";

import TutorialsPage from "@/pages/account/TutorialsPage";

import IntroTutorial from "@/components/tutorial/IntroTutorial";
import TabellPage from "./pages/TabellPage";
import LivePage from "./pages/LivePage";
import LiveDetailPage from "./pages/LiveDetailPage";
import SeriesPage from "./pages/SeriesPage";


import PushPermissionPrompt from "@/components/pwa/PushPermissionPrompt";
import InstallAppPrompt from "@/components/pwa/InstallAppPrompt";
import CookieBanner from "@/components/CookieBanner";
import PrivacyConsentRuntime from "@/components/PrivacyConsentRuntime";
import AccountComplianceGate from "@/components/AccountComplianceGate";
import NativeRuntimeBridge from "@/components/native/NativeRuntimeBridge";
import NativeOnboarding from "@/components/native/NativeOnboarding";
import { isNativeApp } from "@/lib/runtime";
import { NativeRouteSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";
import { NativeAppUpdateProvider } from "@/contexts/NativeAppUpdateContext";

export default function App() {
  const native = isNativeApp();

  useEffect(() => {
    if (native || typeof window === "undefined" || !("serviceWorker" in navigator)) return undefined;

    const closeLapstrNotifications = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        if (typeof registration.getNotifications !== "function") return;
        const notifications = await registration.getNotifications();
        notifications.forEach((notification) => {
          try {
            notification.close();
          } catch {
            // ignore close errors
          }
        });
      } catch {
        // ignore unsupported browsers / SW not ready
      }
    };

    const handleForegroundPush = (event) => {
      const payload = event?.data?.payload;
      if (!payload) return;

      void queryClient.invalidateQueries({ queryKey: ["notifications", "unreadCount"] });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "unseenCount"] });
      void queryClient.invalidateQueries({ queryKey: ["notifications", "inbox"] });

      if (payload.notificationType === "lineup_changed" || payload.notificationType === "lineup_published") {
        return;
      }

      const title = payload.title || "Lapstr";
      const description = payload.body || "";
      toast(title, {
        description,
      });
    };

    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        closeLapstrNotifications();
      }
    };

    closeLapstrNotifications();
    window.addEventListener("focus", closeLapstrNotifications);
    window.addEventListener("pageshow", closeLapstrNotifications);
    document.addEventListener("visibilitychange", handleVisible);
    navigator.serviceWorker.addEventListener("message", handleForegroundPush);

    return () => {
      window.removeEventListener("focus", closeLapstrNotifications);
      window.removeEventListener("pageshow", closeLapstrNotifications);
      document.removeEventListener("visibilitychange", handleVisible);
      navigator.serviceWorker.removeEventListener("message", handleForegroundPush);
    };
  }, [native]);

  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <Suspense
          fallback={native
            ? window.location.pathname === "/"
              ? <div className="min-h-screen bg-background" />
              : <NativeRouteSkeleton />
            : <div className="p-6">Laddar…</div>}
        >
          <Router>
            <NativeAppUpdateProvider>
              <NativeRuntimeBridge />
              <PrivacyConsentRuntime />
              <AccountComplianceGate />
              {native ? <NativeOnboarding /> : null}
              {/* Här */}
              <ScrollManager />
              {!native && <IntroTutorial />}
              <CookieBanner />
              {!native && <InstallAppPrompt />}
              {!native && <PushPermissionPrompt />}
              <Routes>
              {/* Öppen route för inlogg/registrering */}
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
              <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
              <Route path="/om-lapstr" element={<AboutPublicPage />} />
              <Route path="/legal" element={<LegalPage />} />
              {/* Skyddade routes: kräver inloggning */}
              {/* <Route
                element={
                  <RequireAuth>
                    <RootLayout />
                  </RequireAuth>
                }
              >
                <Route path="/" element={<HomePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/matches" element={<MatchesPage />} />
                <Route path="/account/matches" element={<MyMatchesPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route> */}
              {/* Admin routes: kräver inloggning + admin */}
              <Route element={<RequireAuth />}>
                <Route path="/admin" element={<RequireAdmin><AdminHomePage /></RequireAdmin>} />
                <Route path="/admin/ads" element={<RequireAdmin><AdsListPage /></RequireAdmin>} />
                <Route path="/admin/ads/new" element={<RequireAdmin><AdFormPage /></RequireAdmin>} />
                <Route path="/admin/ads/:adId/edit" element={<RequireAdmin><AdFormPage /></RequireAdmin>} />
                <Route path="/admin/ads/customers/:customerName" element={<RequireAdmin><AdCustomerDetailPage /></RequireAdmin>} />
                <Route path="/admin/lineups" element={<RequireAdmin><AdminLineupsPage /></RequireAdmin>} />
                <Route path="/admin/guest-riders" element={<RequireAdmin><AdminGuestRidersPage /></RequireAdmin>} />
                <Route path="/admin/notifications" element={<RequireAdmin><AdminNotificationsPage /></RequireAdmin>} />
                <Route path="/admin/match-structure" element={<RequireAdmin><AdminMatchStructurePage /></RequireAdmin>} />
                <Route path="/admin/double-meeting-bonus" element={<RequireAdmin><AdminDoubleMeetingBonusPage /></RequireAdmin>} />
                <Route path="/admin/playoff-series-resolutions" element={<RequireAdmin><AdminPlayoffSeriesResolutionsPage /></RequireAdmin>} />
                <Route path="/admin/app-releases" element={<RequireAdmin><AdminAppReleasesPage /></RequireAdmin>} />
              </Route>


              <Route
                element={
                  <RequireAuth>
                    <RootLayout />
                    {/* <SessionGuard /> */}
                  </RequireAuth>
                }
              >
                <Route path="/" element={<HomePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />

                {/* /account som layout + undersidor */}
                <Route path="/account" element={<AccountPage />}>
                  <Route path="matches" element={<MatchProgramsPage />} />
                  <Route path="matches/:tab" element={<MatchProgramsPage />} />
                  <Route path="matches/saved/:userMatchId" element={<SavedMatchDetailPage />} />
                  <Route path="protocol" element={<ProtocolSettingsPage />} />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route path="appearance" element={<AppearancePage />} />
                  <Route path="tutorials" element={<TutorialsPage />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="announcements" element={<AnnouncementsPage />} />
                  <Route path="security" element={<SecurityPage />} />
                  <Route path="privacy" element={<PrivacyPage />} />
                  <Route path="legal" element={<LegalPage />} />
                  <Route path="about" element={<AboutPage />} />
                </Route>

                <Route path="/matches" element={<MatchesPage />} />
                <Route path="/matches/official/:officialId" element={<OfficialMatchDetailPage />} />
                <Route path="/live" element={<LivePage />} />
                <Route path="/live/:officialId" element={<LiveDetailPage />} />
                <Route path="/serie" element={<SeriesPage />} />
                <Route path="/serie/lag/:teamId" element={<SeriesPage />} />
                {/* sålänge rendera komponent sålänge.... */}
                <Route path="/tabell" element={<TabellPage />} />
                <Route path="/tabell/:tab" element={<TabellPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
              </Routes>
            </NativeAppUpdateProvider>
            {/* 2. Lägg till Toaster-komponenten här */}
            <Toaster
              position="top-center"
              offset={{
                top: "calc(env(safe-area-inset-top) + 1rem)",
                left: "1rem",
                right: "1rem",
              }}
              mobileOffset={{
                top: "calc(env(safe-area-inset-top) + 1rem)",
                left: "0.75rem",
                right: "0.75rem",
              }}
            />
          </Router>
        </Suspense>
      </AuthProvider>
    </ThemeProvider>
  );
}


